import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import api from '../services/api';

const PayoutCalculator = ({ open, vendor, onClose, onProceed }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(vendor?.id || '');
  const [payoutData, setPayoutData] = useState(null);

  useEffect(() => {
    if (!vendor) {
      fetchVendors();
    }
  }, [vendor]);

  useEffect(() => {
    if (selectedVendor) {
      calculatePayout();
    }
  }, [selectedVendor]);

  const fetchVendors = async () => {
    try {
      const response = await api.get('/api/admin/clients');
      setVendors(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const calculatePayout = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/api/consignments/payouts/calculate', {
        params: { vendor_id: selectedVendor },
      });
      setPayoutData(response.data);
    } catch (error) {
      console.error('Error calculating payout:', error);
      setError(error.response?.data?.error || 'Error calculating payout');
    } finally {
      setLoading(false);
    }
  };

  const handleProceed = () => {
    if (payoutData && payoutData.items.length > 0) {
      onProceed({
        ...payoutData,
        consignment_ids: payoutData.items.map((item) => item.consignment_id),
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Calculate Vendor Payout</DialogTitle>
      <DialogContent>
        {!vendor && (
          <FormControl fullWidth sx={{ mb: 3, mt: 1 }}>
            <InputLabel>Select Vendor</InputLabel>
            <Select
              value={selectedVendor}
              onChange={(e) => setSelectedVendor(e.target.value)}
              label="Select Vendor"
            >
              {vendors.map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {v.user?.first_name} {v.user?.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : payoutData ? (
          <>
            {payoutData.items.length === 0 ? (
              <Alert severity="info">No items available for payout for this vendor.</Alert>
            ) : (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Vendor:</strong> {payoutData.vendor.name}
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Email:</strong> {payoutData.vendor.email}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>
                  Items for Payout
                </Typography>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell align="right">Sale Price</TableCell>
                        <TableCell align="right">Commission</TableCell>
                        <TableCell align="right">Payout</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payoutData.items.map((item) => (
                        <TableRow key={item.consignment_id}>
                          <TableCell>{item.item_name}</TableCell>
                          <TableCell align="right">
                            ${parseFloat(item.actual_sale_price).toFixed(2)}
                          </TableCell>
                          <TableCell align="right">{item.commission_rate}%</TableCell>
                          <TableCell align="right">
                            <strong>${parseFloat(item.payout_amount).toFixed(2)}</strong>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={3} align="right">
                          <Typography variant="h6">Total Payout:</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="h6" color="success.main">
                            ${parseFloat(payoutData.total_amount).toFixed(2)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Items: {payoutData.items_count}
                  </Typography>
                </Box>
              </>
            )}
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleProceed}
          variant="contained"
          disabled={!payoutData || payoutData.items.length === 0}
        >
          Proceed to Payment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PayoutCalculator;
